/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 3:06:36 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**
 *
 * Utility object for Metadata about fields. The exported object can be used to do validations in the source code
 * according to the type. e.g. a field of type file can be used to determine the encoding of the api data as we have
 *  done in APIDataParser.
 *
 * Another example is you could use this to do some action if a value should be double etc.
 *
 * Can be combined with YUP test functions.
 *
 */

import { schemaBuilder, validateFileSize } from "lib/validation";
import { formatBytes } from "lib/utils";

/**
 *
 * Accepted Field Types
 * @type File: Any document including images
 *
 * @type nestedone2many: Field for one2many cardinality, allowing users to add multiple items to the primary object.
 * This field supports and works best with Django's Nested Writable Serializers for both JSON and multipart media types
 *
 * @type one2many: Same as above but is not nested, values are a list of primary keys instead of objects
 *
 * @type many2one: Opposite of above and can be a simple select dropdown
 *
 * @type many2may: many2may cardinality
 *
 */
export enum FIELD_ACCEPTED_TYPES {
  file = "file",
  string = "string",
  integer = "integer",
  double = "double",
  boolean = "boolean",
  date = "date",
  datetime = "datetime",
  time = "time",
  many2one = "many2one",
  one2many = "one2many",
  nestedone2many = "nestedone2many",
  many2many = "many2many",
}

type FIELD_META_DATA = {
  [key in FIELD_ACCEPTED_TYPES]?: { type: `${FIELD_ACCEPTED_TYPES}` };
};

// Composing the metadata about a field so it can be easily added to Yup's meta/describe feature
export const OS_FIELD_META_DATA: FIELD_META_DATA = Object.keys(FIELD_ACCEPTED_TYPES).reduce(
  (prev, curr) => Object.assign(prev, { [curr]: { type: FIELD_ACCEPTED_TYPES[curr] } }),
  {}
);

/**
 * BASE SCHEMAS
 */

export const portNumberBaseSchema = () => {
  return schemaBuilder
    .number()
    .test(
      "port-number-validator",
      "Valid port number must be an integer above 0. Note: For TCP, 0 is reserved and cannot be used as a port number.",
      (v) => v > 0
    );
};

/**
 *
 * @param sizeLimit Limit of file size in bytes e.g. 1MB = 1048576 bytes so pass 1048576
 * @param message Custom message to be shown to the user
 * @returns
 */
interface fileBaseSchemaArgs {
  sizeLimit: number;
  message?: string;
}

export const fileBaseSchema = (args: fileBaseSchemaArgs) => {
  const { message, sizeLimit } = args;

  return schemaBuilder
    .mixed() // A file should be of mixed type in Yup. If you try object, it will return null for some reason
    .meta(OS_FIELD_META_DATA.file)
    .test(
      "file-size-check",
      message ?? `The uploaded file should be less than ${formatBytes(sizeLimit)}`,
      (v) => !sizeLimit || validateFileSize(v, sizeLimit)
    );
};

export const Many2OneIntPKBaseSchema = () => {
  return schemaBuilder.number().meta(OS_FIELD_META_DATA.many2one);
};

export const Many2OneUUIDPKBaseSchema = (uuidValidationMessage?: string) => {
  return schemaBuilder
    .string()
    .uuid(uuidValidationMessage ?? "Value must be a valid UUID")
    .meta(OS_FIELD_META_DATA.many2one)
    .typeError(uuidValidationMessage ?? "Value must be a valid UUID");
};

export const One2ManyIntPKBaseSchema = () => {
  return schemaBuilder.array(schemaBuilder.number()).meta(OS_FIELD_META_DATA.one2many);
};

export const One2ManyUUIDPKBaseSchema = (message?: string) => {
  return schemaBuilder
    .array(schemaBuilder.string().uuid(message ?? "Value must be a valid UUID"))
    .meta(OS_FIELD_META_DATA.one2many);
};

interface nestedOne2ManyBaseSchemaArgs {
  relatedSchema: schemaBuilder.AnyObjectSchema;
}

export const nestedOne2ManyBaseSchema = (args: nestedOne2ManyBaseSchemaArgs) => {
  const { relatedSchema } = args;

  return schemaBuilder.array(relatedSchema).meta(OS_FIELD_META_DATA.nestedone2many);
};

export const Many2ManyIntPKBaseSchema = () => {
  return schemaBuilder.array(schemaBuilder.number()).meta(OS_FIELD_META_DATA.many2many);
};

export const Many2ManyUUIDPKBaseSchema = (message?: string) => {
  return schemaBuilder
    .array(schemaBuilder.string().uuid(message ?? "Value must be a valid UUID"))
    .meta(OS_FIELD_META_DATA.many2many);
};
